import Cookie from "js-cookie";
import ICookieDetails from "../models/account/ICookieDetails";
import IJwtToken from "../models/security/IJwtToken";

export default class AuthCookieProvider {
    public static readonly CookieName = "AuthenticatedUser";

    /// Auth cookies can only set for one of these domains
    private static validDomains = [
        "localhost",
        "clhbid-dev.com",
        "clhbid-test.com",
        "clhbid.com"
    ];

    /**
     * Sets an authentication cookie that will be shared between the auction site
     * and the sales site.
     *
     * The JWT provides the expiry time in seconds, so it should be straight forward
     * to create the cookie from the token: `AuthCookieProvider.SetCookie(jwt.given_name, jwt.expires);`
     *
     * @param user The name of the logged in user
     * @param expiresAtSeconds The time at which the token should expire
     */
    public static SetCookie(token: IJwtToken) {
        const domain = AuthCookieProvider.validDomains.find((d) =>
            window.location.hostname.endsWith(d)
        );
        const expiresAtSeconds = token.exp;

        const cookieDetails: ICookieDetails = {
            UserName: token.given_name,
            Admin: token.roles?.includes("Administrator"),
            ID: token.nameid,
            PhoneNumber: token.phone_number,
            Email: token.sub
        };

        Cookie.set(
            AuthCookieProvider.CookieName,
            AuthCookieProvider.encode(cookieDetails),
            {
                sameSite: "none",
                secure: true,
                path: "/",
                domain: "." + domain,
                expires: new Date(expiresAtSeconds * 1000) // convert seconds to milliseconds when calling new Date
            }
        );
    }

    /**
     * Base64 of the details' UTF-8 JSON. `btoa` only takes Latin-1, so names
     * and emails outside it are encoded to UTF-8 bytes first. clhbid.com reads
     * both this and the older Latin-1 cookies.
     */
    public static encode(cookieDetails: ICookieDetails): string {
        const bytes = new TextEncoder().encode(JSON.stringify(cookieDetails));
        return btoa(String.fromCharCode(...bytes));
    }

    public static DeleteCookie() {
        const domain = AuthCookieProvider.validDomains.find((d) =>
            window.location.hostname.endsWith(d)
        );

        Cookie.remove(AuthCookieProvider.CookieName, {
            sameSite: "none",
            secure: true,
            path: "/",
            domain: "." + domain
        });
    }
}
